import type {
  FeeCategory,
  FeeItem,
} from '@/apis/extended/admin/interestRateApi';
import {
  CALCULATED_FEE_CATALOG,
  type CalculatedFeeCatalogItem,
  type CalculatedFeeKey,
} from '@/utils/fees/calculatedFeeCatalog';
import type { StateTaxLine } from '@/utils/fees/stateClosingTaxes';
import type { ProductEsitmation } from '@/types/ProductType';

export type CalculatedFeeRule = {
  key: CalculatedFeeKey;
  label: string;
  description?: string;
  category: FeeCategory;
  visible: boolean;
  includedInApr: boolean;
};

export type ComputedFeeLine = FeeItem & {
  computedAmount: number;
  /** Listed for transparency but not in totalClosingCosts. */
  notPaidAtClosing?: string;
};

export type FeeBreakdown = {
  byCategory: Record<FeeCategory, ComputedFeeLine[]>;
  totalClosingCosts: number;
  totalAprFees: number;
};

export function computeFeeAmount(fee: FeeItem, loanAmount: number): number {
  const base =
    fee.amountType === 'percentOfLoan'
      ? (Number(fee.amount) / 100) * loanAmount
      : Number(fee.amount);
  const signed = fee.sign === 'subtract' ? -base : base;
  return Number.isFinite(signed) ? signed : 0;
}

export function computeFeeBreakdown(
  feeItems: FeeItem[] | undefined,
  loanAmount: number
): FeeBreakdown {
  const categories: FeeCategory[] = [
    'lender',
    'title',
    'government',
    'prepaid',
    'other',
  ];
  const byCategory: Record<FeeCategory, ComputedFeeLine[]> = {
    lender: [],
    title: [],
    government: [],
    prepaid: [],
    other: [],
  };

  const fees = Array.isArray(feeItems) ? feeItems : [];
  for (const fee of fees) {
    const category = categories.includes(fee.category) ? fee.category : 'other';
    const computedAmount = computeFeeAmount(fee, loanAmount);
    byCategory[category].push({ ...fee, computedAmount });
  }

  const all = categories.flatMap((c) => byCategory[c]);
  const totalClosingCosts = all.reduce((sum, f) => sum + f.computedAmount, 0);
  const totalAprFees = all
    .filter((f) => !!f.includedInApr)
    .reduce((sum, f) => sum + f.computedAmount, 0);

  return {
    byCategory,
    totalClosingCosts,
    totalAprFees,
  };
}

export function computeMergedFeeBreakdown(params: {
  estimation: ProductEsitmation;
  calculatedFeeRules?: CalculatedFeeRule[];
  feeItems?: FeeItem[];
  loanAmount: number;
}): FeeBreakdown {
  const { estimation, calculatedFeeRules, feeItems, loanAmount } = params;
  const categories: FeeCategory[] = [
    'lender',
    'title',
    'government',
    'prepaid',
    'other',
  ];

  // Build rule map (admin overrides)
  const ruleMap = new Map<string, CalculatedFeeRule>();
  (calculatedFeeRules || []).forEach((r) => {
    ruleMap.set(r.key, r);
  });

  // When the calculator reports exactly what it summed, itemize those parts
  // so the list adds up to the closing costs used in cash to close.
  const parts = (estimation as { closingCostParts?: Record<string, number> })
    .closingCostParts;
  const partAmount = (item: CalculatedFeeCatalogItem): number | null => {
    if (!parts) return null;
    const names = item.parts ?? [item.key];
    if (!names.some((n) => n in parts)) return null;
    return names.reduce((sum, n) => sum + (Number(parts[n]) || 0), 0);
  };

  // Convert calculated fees -> FeeItem-like lines so we can reuse the breakdown type
  const calculatedAsFeeItems: (FeeItem & { notPaidAtClosing?: string })[] = [];
  for (const item of CALCULATED_FEE_CATALOG as CalculatedFeeCatalogItem[]) {
    if (item.isApplicable && !item.isApplicable(estimation)) continue;
    const fromParts = partAmount(item);
    // With parts, a line the calculator didn't sum is either informational
    // (financed / paid outside closing) or not part of this estimate.
    if (parts && fromParts == null && !item.notPaidAtClosing) continue;
    const amount = fromParts ?? item.compute(estimation);
    // Skip truly empty fees unless admin explicitly keeps visible
    const rule = ruleMap.get(item.key);
    const visible = rule ? !!rule.visible : item.defaultVisible;
    const includedInApr = rule
      ? !!rule.includedInApr
      : item.defaultIncludedInApr;
    const category = rule?.category || item.category;
    const label = rule?.label || item.label;
    const description = rule?.description || item.defaultDescription;

    if (!visible) continue;
    if (amount === 0 && !rule) continue;

    const outsideClosing = !!item.notPaidAtClosing && fromParts == null;
    calculatedAsFeeItems.push({
      key: `calc_${item.key}`,
      label: outsideClosing ? `${label} (not in cash to close)` : label,
      description: outsideClosing
        ? [item.notPaidAtClosing, description].filter(Boolean).join(' ')
        : description,
      category,
      sign: 'add',
      amountType: 'flat',
      amount,
      includedInApr,
      ...(outsideClosing ? { notPaidAtClosing: item.notPaidAtClosing } : {}),
    });
  }

  // State closing taxes: one line per tax (deed/transfer tax, mortgage or
  // recordation tax…) with its own explanation, instead of a single
  // "Transfer Taxes" line that also left out the borrower's loan taxes.
  const est = estimation as {
    stateTaxLines?: StateTaxLine[];
    stateTaxState?: string;
    stateTaxNote?: string;
  };
  if (Array.isArray(est.stateTaxLines)) {
    const transferIdx = calculatedAsFeeItems.findIndex(
      (f) => f.key === 'calc_transferTaxes'
    );
    const transferRule = ruleMap.get('transferTaxes');
    const showTaxes = transferRule ? !!transferRule.visible : true;
    if (transferIdx >= 0) calculatedAsFeeItems.splice(transferIdx, 1);
    if (showTaxes) {
      for (const line of est.stateTaxLines) {
        if (!(line.buyerAmount > 0)) continue;
        const notes = [
          line.description,
          line.buyerAmount < line.totalAmount
            ? `Your share of a ${Math.round(line.totalAmount).toLocaleString(
                'en-US',
                { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }
              )} tax, split by ${est.stateTaxState || 'state'} custom.`
            : '',
          line.confirm
            ? 'Varies by county — your title company will confirm.'
            : '',
          est.stateTaxNote || '',
        ].filter(Boolean);
        calculatedAsFeeItems.push({
          key: `calc_stateTax_${line.key}`,
          label: est.stateTaxState
            ? `${line.label} (${est.stateTaxState})`
            : line.label,
          description: notes.join(' '),
          category: transferRule?.category || 'government',
          sign: 'add',
          amountType: 'flat',
          amount: line.buyerAmount,
          includedInApr: transferRule ? !!transferRule.includedInApr : false,
        });
      }
    }
  }

  // Combine calculated fees + admin adjustments (feeItems)
  const combined = [...calculatedAsFeeItems, ...(feeItems || [])];

  // Compute breakdown (reuses existing logic)
  const byCategory: Record<FeeCategory, ComputedFeeLine[]> = {
    lender: [],
    title: [],
    government: [],
    prepaid: [],
    other: [],
  };

  for (const fee of combined) {
    const category = categories.includes(fee.category) ? fee.category : 'other';
    const computedAmount =
      fee.key.startsWith('calc_') && fee.amountType === 'flat'
        ? Number(fee.amount) || 0
        : computeFeeAmount(fee, loanAmount);
    byCategory[category].push({ ...fee, computedAmount });
  }

  const all = categories
    .flatMap((c) => byCategory[c])
    .filter((f) => !f.notPaidAtClosing);
  const totalClosingCosts = all.reduce((sum, f) => sum + f.computedAmount, 0);
  const totalAprFees = all
    .filter((f) => !!f.includedInApr)
    .reduce((sum, f) => sum + f.computedAmount, 0);

  return { byCategory, totalClosingCosts, totalAprFees };
}
